<template>
  <div
    v-if="modelValue || showInvalidWarning"
    class="record-field"
  >
    <div :class="{ 'invalid-field': showInvalidWarning }">
      <h2
        class="h5--ssp"
        :class="{ 'invalid-field__label': showInvalidWarning }"
      >
        {{ $t(`fields.record.${fieldName}.label`) }}
      </h2>
      <div
        v-if="showInvalidWarning"
        class="invalid-field__warn"
      >
        <svg
          height="18"
          width="18"
          viewBox="0 0 18 18"
          xmlns="http://www.w3.org/2000/svg"
        >
          <g
            stroke="var(--orange)"
            fill="none"
            fill-rule="evenodd"
          >
            <circle
              stroke-width="1.046"
              cx="9"
              cy="9"
              r="8.477"
            />
            <path
              d="M7.2 6.514c0-1.254.99-1.984 1.98-2.013.99-.03 1.98.66 1.98 1.894 0 1.999-2.148 1.122-2.2 3.625"
              stroke-width="1.046"
              stroke-linecap="round"
            />
            <path
              d="M9 13.185a.54.54 0 1 1 0-1.08.54.54 0 0 1 0 1.08Z"
              stroke-width=".25"
              fill="var(--orange)"
            />
          </g>
        </svg>
        <span>
          {{ $t('record.view.inconsistency.label') }}
        </span>
      </div>
    </div>
    <p>
      <slot>
        <template v-if="!invalid">
          <template v-if="fieldName === 'doi'">
            <a
              :href="`https://doi.org/${modelValue.trim()}`"
              target="_blank"
            >
              {{ modelValue.trim() }}
            </a>
          </template>
          <template v-else-if="fieldName === 'pmid'">
            <a
              :href="`https://pubmed.ncbi.nlm.nih.gov/${modelValue.trim()}`"
              target="_blank"
            >
              {{ modelValue.trim() }}
            </a>
          </template>
          <template v-else-if="fieldName === 'wos'">
            <a
              :href="`https://www.webofscience.com/wos/woscc/full-record/${modelValue.trim()}`"
              target="_blank"
            >
              {{ modelValue.trim() }}
            </a>
          </template>
          <template v-else-if="isInternalLink">
            <a :href="internalLinkPath">{{ valueFromAPI ?? modelValue.trim() }}</a>
          </template>
          <!-- eslint-disable vue/no-v-html -->
          <p
            v-else
            v-html="output"
          />
          <!-- eslint-enable vue/no-v-html -->
        </template>
        <template v-else>
          {{ $t('record.view.inconsistency.value') }}
        </template>
      </slot>
    </p>
  </div>
</template>

<script>
import { escape as htmlEscape } from 'html-escaper'
import useRecordsAPI from '#src/api/records'

export default {
  inject: ['ignoredFields', '$axios', '$baseUrl'],
  props: {
    modelValue: {
      type: String,
      default: () => ''
    },
    fieldName: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      latex2utf8: null,
      RecordsAPI: useRecordsAPI(this.$axios),
      valueFromAPI: null
    }
  },
  computed: {
    invalid() {
      if (this.ignoredFields) {
        return this.ignoredFields.includes(this.fieldName)
      }
      return false
    },
    showInvalidWarning() {
      return this.invalid && this.$auth?.isLogged()
    },
    isInternalLink() {
      return this.modelValue?.startsWith(this.$baseUrl)
    },
    internalLinkPath() {
      return this.isInternalLink ? new URL(this.modelValue).pathname : null
    },
    output() {
      const safeValue = htmlEscape(this.modelValue.trim())
      if (!this.latex2utf8) {
        return safeValue
      } else {
        return this.latex2utf8(safeValue)
      }
    }
  },
  async created() {
    const { latex2utf8 } = await import('#src/utils/Latex2Utf8')
    this.latex2utf8 = latex2utf8
  },
  async mounted() {
    if (this.internalLinkPath?.startsWith('/records/')) {
      const recordId = this.internalLinkPath.split('/')[2]
      try {
        const record = await this.RecordsAPI.get({ id: recordId })
        this.valueFromAPI = record.title
      } catch {
        console.log('Invalid record ID: ' + recordId)
      }
    }
  }
}
</script>
<style lang="scss" scoped>
@use "#src/assets/scss/record" as *;
</style>
